import { StatusCode } from './uploadsActions';
import config from '../../config';
import { authFetch } from '../../common/authentication';
import { getLogUrl, getLogStatus } from '../../api/ingest';

const SFTP_UPLOAD_AGENT = 'SFTP';

export default function uploadsMiddleware(store) {
  return (next) => (action) => {
    switch (action.type) {
      case 'uploads/getuploads':
        store.dispatch({
          type: 'uploads/update',
          payload: { status: StatusCode.Loading },
        });
        getUploads(action.payload).then((result) => {
          store.dispatch({
            type: 'uploads/update',
            payload: { records: result, status: StatusCode.Loaded },
          });
        });
        break;

      case 'uploads/openuploaddetails':
        store.dispatch({
          type: 'uploads/update',
          payload: {
            openuploaddetails: {
              status: 'open',
              upload: action.payload,
            },
          },
        });
        break;

      case 'uploads/closeuploaddetails':
        store.dispatch({
          type: 'uploads/update',
          payload: {
            openuploaddetails: null,
          },
        });
        break;

      case 'uploads/stagedelete':
        store.dispatch({
          type: 'uploads/update',
          payload: {
            delete: {
              status: 'prompt',
              upload: action.payload,
            },
          },
        });
        break;

      case 'uploads/canceldelete':
        store.dispatch({
          type: 'uploads/update',
          payload: { delete: null },
        });
        break;

      case 'uploads/downloadlogs':
        store.dispatch({
          type: 'uploads/update',
          payload: { downloadLogs: { upload, status: 'progress' } },
        });

        downloadLogs().then((result) => {});
        break;

      case 'uploads/downloaderror':
        store.dispatch({
          type: 'uploads/update',
          payload: { downloaderror: { status: 'true' } },
        });
        break;

      case 'uploads/closedownloaderror':
        store.dispatch({
          type: 'uploads/update',
          payload: { downloaderror: null },
        });
        break;

      case 'uploads/delete':
        // todo: refactor verbose references
        const upload = action.payload.upload.upload;
        const token = action.payload.token;

        store.dispatch({
          type: 'uploads/update',
          payload: { delete: { upload, status: 'progress' } },
        });

        deleteIngest(upload, token)
          .then(() => {
            const state = store.getState();
            const idx = state.uploads.records.indexOf(upload);
            let result = state.uploads.records;
            if (idx > -1) {
              const target = state.uploads.records[idx];
              result = state.uploads.records.filter((i) => i !== target);
            }
            store.dispatch({
              type: 'uploads/update',
              payload: { records: result, delete: null },
            });
          })
          .catch((error) => {
            store.dispatch({
              type: 'uploads/update',
              payload: {
                delete: { upload, error: 'error' },
                records: store.getState().uploads.records.map((x) => x),
              },
            });
          });
        break;
      default:
        return next(action);
    }
  };
}

function deleteIngest(upload, token) {
  return new Promise((resolve, reject) => {
    authFetch(config.endpoints.deleteIngest, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ingestId: upload.ingestId,
        groupId: upload.groupId,
      }),
    }).then((response) => {
      if (response.status === 200 || response.status === 204) {
        return resolve();
      }
      reject();
    });
  });
}

function getUploads(payload) {
  return new Promise((resolve, reject) => {
    authFetch(config.endpoints.uploads, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${payload.token}`,
      },
    })
      .then((res) => res.json())
      .then((result) => {
        let records = [];
        if (result && result.length > 0) {
          // Need to allow failed SFTP uploads to show in the GUI
          records = result
            .filter(
              (r) =>
                r.uploadAgent === SFTP_UPLOAD_AGENT ||
                ((r.errors === null ||
                  r.errors.length < 1 ||
                  r.errorsAccepted === true) &&
                  r.rowsMapped > 0),
            )
            .map((r) => {
              let bqIngestStatus = 'FAILED';
              if (r.bigQueryIngest) {
                bqIngestStatus = r.bigQueryIngest.Status;
              }

              let uploadSuccess = true;
              if (
                bqIngestStatus !== 'SUCCESS' ||
                (r.errors !== null && r.errorsAccepted !== true)
              ) {
                uploadSuccess = false;
              }

              let previewHeaders = [];
              let previewRows = [[]];

              if (r.hasHeader && uploadSuccess) {
                previewHeaders = r.previewRows[0].r;
                previewRows = r.previewRows.slice(1).map((p) => p.r);
              } else if (r.previewRows != null && uploadSuccess) {
                previewRows = r.previewRows.slice(0).map((p) => p.r);
              }

              let errorMessage = '';
              let userErrors = [
                'MAPPING_ERROR',
                'UPLOAD_FORMAT_ERROR',
                'FILE_FORMAT_ERROR',
              ];
              if (r.errors !== null && r.errors.length > 0) {
                if (!userErrors.includes(r.errors[0].type)) {
                  errorMessage =
                    'An error has occured. Please check the log files.';
                } else {
                  errorMessage = r.errors[0].message;
                }
              }

              return {
                uploadType: r.uploadType,
                ingestId: r.ingestId,
                groupId: r.groupId,
                uploadDate: new Date(r.createdAt),
                uploadAgent: r.uploadAgent,
                fileName: r.fileName,
                bqStatus: bqIngestStatus,
                errors: errorMessage,
                uploadSucceeded: uploadSuccess,
                firstRecord: null,
                lastRecord: null,
                rows: r.rowsIngested ? r.rowsIngested : r.rowsMapped,
                cols: previewRows[0].length,
                preview: {
                  headers: previewHeaders,
                  rows: previewRows,
                },
                duplicateCount: r.duplicateCount,
                validationErrorCount: r.validationErrorCount,
                fileLevelErrorCount: r.logFileLevelExceptions
                  ? r.logFileLevelExceptions.length
                  : 0,
                logFileLevelExceptions: r.logFileLevelExceptions,
              };
            });
        }
        resolve(records);
      });
  });
}

export function download(record, token) {
  const { ingestId, groupId, fileName } = record;
  return new Promise((resolve, reject) => {
    authFetch(
      `${config.endpoints.downloadFile}?ingestId=${encodeURI(
        ingestId,
      )}&groupId=${encodeURI(groupId)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
      .then((response) => response.json())
      .then((result) =>
        fetch(result.url)
          .then((res) => res.blob())
          .then(
            (blob) =>
              new Promise((resolve, reject) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.download = fileName;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  document.body.removeChild(a);
                }, 1000);
                resolve();
              }),
          ),
      )
      .then(resolve);
  });
}

const downloadRowLevelLogs = (url, fileName) => {
  return fetch(url)
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.download = fileName;
          a.href = url;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
          }, 1000);
          resolve();
        }),
    );
};

const downloadFileLevelLogs = (ingestState, fileName) => {
  const logContent = (ingestState.logFileLevelExceptions ?? []).join('\n');
  const blob = new Blob([logContent], { type: 'text/html' });
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
    }, 1000);
    resolve();
  });
};

const downloadEmptyRowLevelLogs = (fileName) => {
  const logContent = [];
  const blob = new Blob([logContent], { type: 'text/html' });
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
    }, 1000);
    resolve();
  });
};

export async function downloadLogs(record) {
  const { ingestId, groupId } = record;
  const logStatus = await getLogStatus(ingestId, groupId);
  const logsAvailable = logStatus !== undefined;
  if (logStatus === 'ready') {
    const url = (await getLogUrl({ groupId, ingestId })).url;
    if (url !== undefined && logStatus === 'ready') {
      await downloadRowLevelLogs(url, `${ingestId}_rowlevel.log`);
    }
  }
  if (logStatus === 'nologs') {
    downloadEmptyRowLevelLogs(`${ingestId}_rowlevel.log`);
  }
  if (logsAvailable) {
    downloadFileLevelLogs(record, `${ingestId}_filelevel.log`);
  }
  return logsAvailable;
}
